import HiddenInput from "@/components/form/components/HiddenInput";
import { Injector } from "@/components/form/Injector";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row, Space } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateCommissionsBatch } from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { CommissionsBatchList, UpdateCommissionsBatch } from "../../types";

interface Props {
  batch?: CommissionsBatchList;
  open: boolean;
  onFormCancel: VoidFunction;
}

export const UpdateCommissionsBatchForm = ({ batch, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<UpdateCommissionsBatch>();
  // Taken from the form rather than from batch, which the parent clears as soon as the modal starts closing
  const namePrefix = Form.useWatch("name", form)?.substring(0, 10);

  const updateCommissionsBatch = useUpdateCommissionsBatch();

  useEffect(() => {
    if (open && batch) {
      form.setFieldsValue({
        optimisticLockVersion: batch.optimisticLockVersion,
        name: batch.name,
      });
    }
  }, [open, batch, form]);

  const handleFormSubmit = () => {
    if (batch) {
      form
        .validateFields()
        .then((values) =>
          updateCommissionsBatch.mutate(
            { id: batch.id, object: values },
            {
              onSuccess: () => {
                onFormCancel();
              },
              onError: (error) => setErrorsToForm(form, "commissions.batch.attrs", error.violations),
            },
          ),
        )
        .catch(resolveFormValidationError);
    }
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={open}
      title={t("commissions.batch.titles.updateBatch")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updateCommissionsBatch.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="updateCommissionsBatchForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={t("commissions.batch.attrs.name")}
              rules={[validations.size(1, 64), validations.notBlank]}
              normalize={(value) => [namePrefix, value ?? ""].join("-")}
              getValueProps={(value) => ({ value: value?.substring(11) })}
            >
              <Injector>
                {(props) => (
                  <Space.Compact block>
                    <Space.Addon className="nowrap">{namePrefix}</Space.Addon>

                    <Input placeholder={t("commissions.batch.helpers.batchNamePlaceholder")} {...props} />
                  </Space.Compact>
                )}
              </Injector>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
